import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarPlus, ChevronDown, Sparkles } from "lucide-react";
import GoldParticles from "./GoldParticles";
import { MaskedLine } from "./Reveal";
import { CornerFlourish } from "./Ornaments";
import { COUPLE, IMAGES, downloadAllIcs, scrollToId } from "./data";

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section
            id="hero"
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-[100svh] items-center justify-center overflow-hidden"
        >
            <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
                <img
                    src={IMAGES.palace[0]}
                    alt="Royal palace architecture"
                    className="h-full w-full object-cover opacity-30"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-obsidian/90 via-obsidian/75 to-obsidian" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(10,30,23,0.72)_80%)]" />
            </motion.div>

            <GoldParticles density={80} />

            <div className="pointer-events-none absolute inset-4 sm:inset-7" aria-hidden="true">
                <div className="h-full w-full border border-gold/25" />
                <div className="absolute inset-2 border border-gold/10" />
                <CornerFlourish className="absolute -left-1 -top-1 h-16 w-16 sm:h-24 sm:w-24" />
                <CornerFlourish className="absolute -right-1 -top-1 h-16 w-16 rotate-90 sm:h-24 sm:w-24" />
                <CornerFlourish className="absolute -bottom-1 -right-1 h-16 w-16 rotate-180 sm:h-24 sm:w-24" />
                <CornerFlourish className="absolute -bottom-1 -left-1 h-16 w-16 -rotate-90 sm:h-24 sm:w-24" />
            </div>

            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 flex flex-col items-center px-6 pb-24 pt-28 text-center"
            >
                <MaskedLine delay={0.15}>
                    <span
                        data-testid="hero-invocation"
                        className="font-devanagari text-base text-gold sm:text-lg"
                    >
                        ॥ श्री गणेशाय नमः ॥
                    </span>
                </MaskedLine>

                <MaskedLine delay={0.35} className="mt-8">
                    <span className="font-cinzel text-[11px] font-semibold uppercase tracking-[0.5em] text-sand sm:text-xs">
                        With Immense Joy &amp; Happiness
                    </span>
                </MaskedLine>

                <MaskedLine delay={0.55} className="mt-6">
                    <span className="font-cinzel text-xs uppercase tracking-[0.4em] text-mutedgold sm:text-sm">
                        The Royal Wedding of
                    </span>
                </MaskedLine>

                <h1 className="mt-4" data-testid="hero-couple-names">
                    <MaskedLine delay={0.75}>
                        <span className="gold-shimmer animate-shimmer-sweep font-script text-6xl leading-tight sm:text-7xl lg:text-8xl">
                            {COUPLE.groom}
                        </span>
                    </MaskedLine>
                    <MaskedLine delay={0.95}>
                        <span className="my-1 block font-royal text-2xl italic text-gold/90 sm:text-3xl">
                            weds
                        </span>
                    </MaskedLine>
                    <MaskedLine delay={1.15}>
                        <span className="gold-shimmer animate-shimmer-sweep font-script text-6xl leading-tight sm:text-7xl lg:text-8xl">
                            {COUPLE.bride}
                        </span>
                    </MaskedLine>
                </h1>

                <MaskedLine delay={1.4} className="mt-8">
                    <span className="flex items-center gap-4">
                        <span className="h-px w-10 bg-gold/60 sm:w-16" />
                        <span
                            data-testid="hero-wedding-date"
                            className="font-cinzel text-sm font-semibold uppercase tracking-[0.3em] text-gold sm:text-base"
                        >
                            Sunday · 22 November 2026
                        </span>
                        <span className="h-px w-10 bg-gold/60 sm:w-16" />
                    </span>
                </MaskedLine>

                <MaskedLine delay={1.55} className="mt-2">
                    <span className="font-cinzel text-[11px] uppercase tracking-[0.45em] text-mutedgold">
                        New Delhi
                    </span>
                </MaskedLine>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.85, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-12 flex flex-col items-center gap-4 sm:flex-row"
                >
                    <button
                        data-testid="hero-view-celebrations-button"
                        onClick={() => scrollToId("invitation")}
                        className="group relative overflow-hidden rounded-full bg-gold px-9 py-3.5 font-cinzel text-[11px] font-bold uppercase tracking-[0.3em] text-obsidian transition-transform duration-500 hover:scale-[1.04]"
                    >
                        <span className="relative z-10 flex items-center gap-2.5">
                            <Sparkles size={14} />
                            Unfold The Invitation
                        </span>
                        <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-gold-glow/70 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                    </button>
                    <button
                        data-testid="add-to-calendar-button"
                        onClick={downloadAllIcs}
                        className="flex items-center gap-2.5 rounded-full border border-gold/50 px-9 py-3.5 font-cinzel text-[11px] font-bold uppercase tracking-[0.3em] text-gold transition-colors duration-500 hover:bg-gold/10"
                    >
                        <CalendarPlus size={14} />
                        Add to Calendar
                    </button>
                </motion.div>
            </motion.div>

            <motion.button
                data-testid="hero-scroll-down"
                onClick={() => scrollToId("invitation")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.4, duration: 1 }}
                className="absolute bottom-10 left-1/2 z-10 -translate-x-1/2 text-gold/80 transition-colors hover:text-gold"
                aria-label="Scroll down"
            >
                <ChevronDown size={26} className="animate-soft-float" />
            </motion.button>
        </section>
    );
};

export default Hero;
